import { PLAYLIST_MAX_ITEMS, countSlides, hasUnknownSlideCount } from '../../../utils/playlist';

// Copy ids refer to devdocs/plans/t240-playlist-ux-spec.md §7.

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export const CAP_WARNING_AT = 45;

// N-1
export const countLine = (items) => {
  const slides = countSlides(items);
  const slideText = hasUnknownSlideCount(items)
    ? `${slides}+ slides`
    : plural(slides, 'slide', 'slides');
  const itemText = items.length >= CAP_WARNING_AT
    ? `${items.length} of ${PLAYLIST_MAX_ITEMS} items`
    : plural(items.length, 'item', 'items');
  return `${itemText} · ${slideText}`;
};

// N-2
export const slideCaption = (pageCount) =>
  (Number.isInteger(pageCount) ? plural(pageCount, 'slide', 'slides') : 'Slides: unknown');

// K-10
export const tileName = (item, position, total) =>
  `Position ${position} of ${total}: ${item.title}, ${slideCaption(item.pageCount)}`;

// K-9
export const carouselHeading = (n) => `Service playlist, ${plural(n, 'item', 'items')}`;

// K-4
export const sheetHandleLabel = (n, expanded) =>
  `Playlist, ${plural(n, 'item', 'items')}. ${expanded ? 'Collapse' : 'Expand'}`;

export const announce = {
  added: (title, n) => `Added ${title}. Position ${n} of ${n}.`, // A-1
  moved: (title, position, total) => `Moved ${title} to position ${position} of ${total}`, // A-2
  alreadyFirst: (title) => `${title} is already first`, // A-3
  alreadyLast: (title) => `${title} is already last`, // A-4
  removed: (title, left) => `Removed ${title}. ${plural(left, 'item', 'items')} left.`, // A-5
  restored: (title, position) => `Restored ${title} to position ${position}`, // A-5u
  full: () => `Playlist is full: ${PLAYLIST_MAX_ITEMS} items`, // A-9
  pickedUp: (title) => `Picked up ${title}`, // A-10
  dropped: (title, position, total) => `Dropped ${title} at position ${position} of ${total}`, // A-10
  sent: (title, k, n) => `Sent ${title}, ${k} of ${n}`, // A-6
  building: (station) => `Building the presentation on ${station}`, // A-6b
  delivered: (n, station) => `Delivered. ${plural(n, 'section', 'sections')} ready in ProPresenter on ${station}.`, // A-7
};

export const COPY = {
  subtitle: "Put this Sunday's slides in order, then deliver them to ProPresenter.", // K-2
  nameLabel: 'Playlist name', // K-5
  dateLabel: 'Service date', // K-6
  pickerHeading: 'Add designs', // K-7
  addTooltip: 'Add to playlist', // K-8v
  reorderHelp: 'Use the left and right arrow keys to move this item. Home and End move it to the start or end. Delete removes it. Alt+P jumps to the playlist.', // K-11
  skipLink: 'Skip to playlist', // K-15
  addTile: 'Add', // K-16
  collapse: 'Collapse playlist', // K-17
  expand: 'Expand playlist', // K-17
  clear: 'Clear', // K-18
  dismiss: 'Dismiss', // K-19
  restoredAction: 'Start a new playlist', // B-1a
  interrupted: 'The last delivery was interrupted. Deliver again to be sure ProPresenter has the latest.', // B-2
  canvaDisconnected: 'Canva is disconnected. Your playlist is safe; reconnect to add designs and deliver.', // B-3
  emptyTitle: 'Your playlist is empty', // E-1
  emptyBody: 'Add your first design: search by hymn number or title, or pick from your folders above. The order here is the order ProPresenter will show.', // E-2
  emptyBodyDisconnected: 'Connect Canva to add designs to your playlist.', // E-3
  emptyPeek: 'No items yet · Add designs above', // E-4
  searchPlaceholder: 'Search by number or title — try 114 or Welcome', // P-1
  searchLabel: 'Search designs by number or title', // P-2
  noResultsBody: 'Try a hymn number like 114, or part of the title.', // P-4
  clearSearch: 'Clear search', // P-4a
  deliver: 'Deliver playlist to ProPresenter', // D-1
  deliverShort: 'Deliver', // D-2
  deliverEmpty: 'Add at least one design to deliver.', // D-3
  deliverDisconnected: 'Reconnect Canva to deliver.', // D-4
  deliverBusy: 'Delivery in progress…', // D-5
  cancel: 'Cancel', // D-8
  noStationOnline: 'No Media Station is online. Start Media Station on your presentation computer, then try again.', // S-7
  capNotice: `Playlist is full: ${PLAYLIST_MAX_ITEMS} items. Remove one to add another.`, // L-1
  capToast: `This playlist already has ${PLAYLIST_MAX_ITEMS} items.`, // L-1t
  capTooltip: `Playlist is full (${PLAYLIST_MAX_ITEMS} items)`, // L-2
  lockedTooltip: 'Wait for the delivery to finish', // L-3
  undo: 'Undo', // U-1
  remove: 'Remove', // U-2
  nameEmpty: 'Give the playlist a name.', // V-1
  nameTooLong: 'Keep the name under 120 characters.', // V-2
  searchFailed: 'Search failed. Please try again.', // P-6
};

export const DELIVERY_COPY = {
  confirmTitle: 'Deliver to ProPresenter', // C-1
  stationCaption: 'Choose Media Station', // C-3
  chooseStation: 'Choose a Media Station to deliver to.', // D-6
  online: 'Online', // S-9
  changeLibrary: 'Change library', // S-3a
  goToStations: 'Go to Media Stations', // S-7a
  close: 'Close', // Modal close (aria)
  edited: 'Edited since last delivery', // R-2
  deliverAgain: 'Deliver again', // G-7
  startNew: 'Start a new playlist', // G-7
  waiting: 'Waiting', // T-1
  sent: 'Sent', // T-3
  couldntSend: "Couldn't send", // T-4
  gone: 'No longer in Canva', // T-5
  missing: "Didn't arrive on the station", // T-6
  timeoutDetail: 'It may still finish. Check ProPresenter, or try again.', // F-5d
  notAvailableDetail: "Each design is on the station on its own. We'll let you know when playlists are ready.", // F-7d
  tryLater: 'Try again later', // F-7a
  sessionExpired: 'Your session expired. Sign in again to deliver.', // F-8
  signIn: 'Sign in', // F-8a
  invalid: "This playlist can't be delivered as it is", // F-9
  retry: 'Retry', // F-11
  chooseAnother: 'Choose another station', // F-11
  oldStation: 'This Media Station may need the latest Media Station update.', // F-4u
  remove: 'Remove', // U-2 (inline Remove on a gone tile)
};

export const confirmSummary = (items) => `${countLine(items)}, in this order:`; // C-2
export const confirmLabel = (n, station) => `Deliver ${plural(n, 'item', 'items')} to ${station}`; // D-7
export const offlineStations = (names) => `Offline (${names.length}): ${names.join(', ')}`; // S-6
export const redeliveryNote = (when) => `Replaces the presentation delivered ${when}.`; // R-1
export const sendingLine = (k, n, title) => `Sending item ${k} of ${n} · ${title}`; // G-1
export const buildingLine = (station) => `Building the presentation on ${station}…`; // G-2
export const buildingDetail = (n) => `Combining ${plural(n, 'section', 'sections')} in order`; // G-3
export const successLine = (n, station) => `Delivered · ${plural(n, 'section', 'sections')} · ready in ProPresenter on ${station}`; // G-4
export const successDetail = (libraryText, fileName) => [libraryText, fileName].filter(Boolean).join(' · '); // G-5
export const deliveredAtCaption = (time) => `Delivered ${time}`; // G-6
export const retryGone = (title) => `Remove or replace ${title} to try again.`; // D-9
export const retryOffline = (station) => `Waiting for ${station} to come back online…`; // D-10
export const backOnline = (station) => `${station} is back online`; // F-6b
export const deliveredToast = (playlist, station) => `Delivered "${playlist}" to ${station}.`; // TS-1
export const failedToast = (playlist) => `Delivery of "${playlist}" needs attention.`; // TS-2

// F-1 … F-10: the strip's primary failure line (also the assertive A-8).
export const failureLine = (kind, { title, k, n, station, missing }) => ({
  item: `Couldn't send ${title} (item ${k} of ${n})`, // F-1
  gone: `${title} is no longer in your Canva account`, // F-2
  missing: `${station} couldn't build the presentation: ${plural(missing, 'item', 'items')} didn't arrive`, // F-3
  station: `${station} couldn't build the presentation`, // F-4
  timeout: `${station} didn't answer in time`, // F-5
  offline: `${station} went offline during delivery`, // F-6
  not_available: `Your designs were sent to ${station}, but combining them into one presentation isn't switched on yet.`, // F-7
  auth: DELIVERY_COPY.sessionExpired, // F-8
  invalid: DELIVERY_COPY.invalid, // F-9
  not_found: `${station} is no longer linked to your account`, // F-10
}[kind]);

export const restoredBanner = (dayLabel) => `Restored your playlist from ${dayLabel}.`; // B-1
export const resultsLine = (query, n) => `Results for "${query}" (${n})`; // P-3
export const noResultsTitle = (query) => `No designs match "${query}"`; // P-4
export const addLabel = (title) => `Add ${title} to the playlist`; // K-8
export const moveEarlierLabel = (title) => `Move ${title} earlier`; // K-12
export const moveLaterLabel = (title) => `Move ${title} later`; // K-13
export const removeLabel = (title) => `Remove ${title}`; // K-14
export const removedUndo = (title) => `Removed ${title}`; // U-1
export const removeConfirm = (title) => `Remove ${title} from the playlist?`; // U-2
export const clearConfirm = (n) => `Clear the playlist? This removes ${plural(n, 'item', 'items')}.`; // U-3
export const plannerHeading = (name) => `Service Playlist: ${name}`; // §6.1 page h1

// Saved playlists (M3, spec S8).
export const SAVED_COPY = {
  save: 'Save', // SV-1
  saved: 'Saved', // SV-1
  saving: 'Saving…', // SV-1b
  drawerTitle: 'Saved playlists', // SV-2
  newPlaylist: 'New playlist', // SV-3
  draft: 'Draft', // SV-3b
  delivered: 'Delivered', // SV-3b
  open: 'Open', // SV-3c
  delete: 'Delete', // SV-3c
  current: 'Open now', // SV-3d
  notDelivered: 'Not delivered yet', // SV-3a
  empty: 'No saved playlists yet. Save this one to reuse it next week.', // SV-4
  error: "Couldn't load your saved playlists.", // SV-5
  retry: 'Retry', // SV-5
  notConfigured: "Saving playlists isn't switched on yet. Your current playlist stays in this browser.", // SV-6
  replaceTitle: 'Replace your current playlist?', // SV-8
  saveFirst: 'Save it first', // SV-8
  replace: 'Replace', // SV-8
  newTitle: 'Start a new playlist? This one has unsaved changes.', // SV-8b
  startNew: 'Start new', // SV-8b
  conflict: 'This playlist changed on another device.', // SV-9
  keepMine: 'Keep mine', // SV-9
  useSaved: 'Use the saved version', // SV-9
  goneTitle: 'This playlist was deleted on another device. Save it as a new playlist?', // SV-11
  saveAsNew: 'Save as new', // SV-11
  close: 'Close', // Drawer close (aria)
};

const relativeTime = (iso, now = Date.now()) => {
  const mins = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${plural(hours, 'hour', 'hours')} ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const savedCaption = (iso, now) => `Saved ${relativeTime(iso, now)}`; // SV-1a
export const savedCount = (n) => plural(n, 'playlist', 'playlists'); // SV-2a
// SV-3a
export const savedRowMeta = ({ serviceDay, itemCount, lastDeliveredAt }, now) => [
  serviceDay,
  plural(itemCount, 'item', 'items'),
  lastDeliveredAt ? `Delivered ${relativeTime(lastDeliveredAt, now)}` : SAVED_COPY.notDelivered,
].filter(Boolean).join(' · ');
export const openLabel = (name) => `Open ${name}`; // SV-3c
export const deleteLabel = (name) => `Delete ${name}`; // SV-3c
export const deleteConfirm = (name) => `Delete "${name}"? This can't be undone.`; // SV-7
export const saveFailed = (reason) => `Couldn't save the playlist: ${reason}`; // SV-10
export const deleteFailed = (reason) => `Couldn't delete the playlist: ${reason}`; // SV-12

export const savedAnnounce = {
  saved: (name) => `Saved ${name}`, // A-11
  opened: (name, n) => `Opened ${name}. ${plural(n, 'item', 'items')}.`, // A-12
  deleted: (name) => `Deleted ${name}`, // A-13
};
